/**
 * Phone, then code. One screen, two steps — a partner who mistypes their
 * number should not lose the screen they were on to get back to it.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { ApiError, api, sessionQuery } from "../lib/api";
import { copy, PARTNER_CONSENT_VERSION, PRIVACY_URL } from "../lib/copy";
import { isValidIndianMobile, normalizeToE164 } from "../lib/phone";

const RESEND_SECONDS = 60;

export function LoginPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	/**
	 * Three steps, but a partner only ever sees two of them.
	 *
	 * `register` is reached ONLY when the API answers 409
	 * `registration_required` — i.e. the code was right and this number is new.
	 * We deliberately do not ask up front whether they are already a partner:
	 * an endpoint that answered that would be an oracle on our partner list,
	 * and a screen that asked would make the partner do our bookkeeping.
	 */
	const [step, setStep] = useState<"phone" | "code" | "register">("phone");
	const [name, setName] = useState("");
	const [firmName, setFirmName] = useState("");
	const [phone, setPhone] = useState("");
	// What the partner typed stays in `phone` for the input; `e164` is the
	// canonical form both calls send, so request-otp and verify-otp can never
	// disagree about which number this is.
	const [e164, setE164] = useState("");
	const [code, setCode] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [secondsLeft, setSecondsLeft] = useState(0);

	useEffect(() => {
		if (secondsLeft <= 0) return;
		const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
		return () => clearTimeout(timer);
	}, [secondsLeft]);

	const sendOtp = useMutation({
		mutationFn: (target: string) => api.requestOtp(target),
		onSuccess: () => {
			toast.success(step === "code" ? copy.login.resent : copy.login.codeSent);
			setStep("code");
			setSecondsLeft(RESEND_SECONDS);
		},
		onError: (e) => setError(messageFor(e)),
	});

	const verify = useMutation({
		mutationFn: () =>
			api.verifyOtp(
				e164,
				code,
				step === "register" && name.trim()
					? {
							name: name.trim(),
							...(firmName.trim() ? { firmName: firmName.trim() } : {}),
							// Sent with every registration, never with a sign-in: this
							// screen is the only one that creates an account, and the
							// account is what they are agreeing to.
							consentVersion: PARTNER_CONSENT_VERSION,
						}
					: undefined,
			),
		onSuccess: (result) => {
			queryClient.setQueryData(sessionQuery.queryKey, {
				authenticated: true,
				name: result.name,
			});
			navigate({ to: "/" });
		},
		onError: (e) => {
			// 409 is not a failure — it is the API telling us this number is new
			// and needs a name. The SAME code stays valid, so the partner types
			// one more field rather than waiting for another WhatsApp message.
			if (e instanceof ApiError && e.code === "registration_required") {
				setStep("register");
				setError(null);
				return;
			}
			setError(
				e instanceof ApiError && e.status === 0
					? copy.errors.network
					: copy.login.verifyFailed,
			);
		},
	});

	function submitPhone(event: React.FormEvent) {
		event.preventDefault();
		const canonical = normalizeToE164(phone);
		if (!canonical || !isValidIndianMobile(phone)) {
			setError(copy.login.phoneInvalid);
			return;
		}
		setError(null);
		setE164(canonical);
		sendOtp.mutate(canonical);
	}

	function submitCode(event: React.FormEvent) {
		event.preventDefault();
		if (!/^\d{6}$/.test(code)) {
			setError(copy.login.codeInvalid);
			return;
		}
		setError(null);
		verify.mutate();
	}

	function submitRegistration(event: React.FormEvent) {
		event.preventDefault();
		if (!name.trim()) {
			setError(copy.login.nameRequired);
			return;
		}
		setError(null);
		verify.mutate();
	}

	return (
		<main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
			<div className="mb-8">
				<h1 className="text-3xl font-semibold tracking-tight">
					{copy.login.title}
				</h1>
				<p className="mt-2 flex items-center gap-2 text-ink-soft">
					<MessageCircle className="size-5 shrink-0" aria-hidden="true" />
					{copy.login.subtitle}
				</p>
			</div>

			{step === "phone" ? (
				<form onSubmit={submitPhone} noValidate>
					<label
						className="block text-sm font-medium text-ink-soft"
						htmlFor="phone"
					>
						{copy.login.phoneLabel}
					</label>
					<div className="mt-2 flex items-stretch overflow-hidden rounded-2xl border-2 border-line bg-card focus-within:border-clay">
						<span className="flex items-center pl-4 pr-2 text-lg text-ink-soft">
							+91
						</span>
						<input
							id="phone"
							type="tel"
							inputMode="numeric"
							autoComplete="tel-national"
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
							placeholder={copy.login.phonePlaceholder}
							className="h-16 w-full bg-transparent pr-4 text-xl tracking-wide outline-none"
							aria-describedby="phone-hint"
						/>
					</div>
					<p id="phone-hint" className="mt-2 text-sm text-ink-soft">
						{copy.login.phoneHint}
					</p>
					<ErrorLine message={error} />
					<PrimaryButton pending={sendOtp.isPending}>
						{sendOtp.isPending ? copy.login.sending : copy.login.sendCode}
					</PrimaryButton>
				</form>
			) : step === "code" ? (
				<form onSubmit={submitCode} noValidate>
					<button
						type="button"
						onClick={() => {
							setStep("phone");
							setCode("");
							setError(null);
						}}
						className="-ml-2 mb-4 flex h-12 items-center gap-2 rounded-xl px-2 text-clay-dark"
					>
						<ArrowLeft className="size-5" aria-hidden="true" />
						{copy.login.changeNumber}
					</button>
					<label
						className="block text-sm font-medium text-ink-soft"
						htmlFor="code"
					>
						{copy.login.codeLabel}
					</label>
					<input
						id="code"
						type="text"
						inputMode="numeric"
						autoComplete="one-time-code"
						maxLength={6}
						// biome-ignore lint/a11y/noAutofocus: the phone step just unmounted — without this, focus falls to <body> and a screen reader loses the step change
						autoFocus
						value={code}
						onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
						className="mt-2 h-16 w-full rounded-2xl border-2 border-line bg-card px-4 text-center text-3xl tracking-[0.5em] outline-none focus:border-clay"
						aria-describedby="code-hint"
					/>
					<p id="code-hint" className="mt-2 text-sm text-ink-soft">
						{copy.login.codeHint}
					</p>
					<ErrorLine message={error} />
					<PrimaryButton pending={verify.isPending}>
						{verify.isPending ? copy.login.verifying : copy.login.verify}
					</PrimaryButton>
					<button
						type="button"
						disabled={secondsLeft > 0 || sendOtp.isPending}
						onClick={() => {
							setError(null);
							sendOtp.mutate(e164);
						}}
						className="mt-4 h-14 w-full rounded-2xl text-clay-dark disabled:text-ink-soft"
					>
						{secondsLeft > 0
							? copy.login.resendIn(secondsLeft)
							: copy.login.resend}
					</button>
				</form>
			) : (
				/*
				 * Registration. Reached only after a CORRECT code on a number we
				 * do not know, so the partner has already proved the handset is
				 * theirs — this asks for the least we can enrol them with.
				 *
				 * No UPI and no PAN here (FR-P-1.3): those are asked for when
				 * there is money to pay, not before anyone has earned anything.
				 */
				<form onSubmit={submitRegistration} noValidate>
					<h2 className="text-xl font-semibold">{copy.login.registerTitle}</h2>
					<p className="mt-2 text-ink-soft">{copy.login.registerBody}</p>

					<label className="mt-6 block font-medium" htmlFor="reg-name">
						{copy.login.nameLabel}
					</label>
					<input
						id="reg-name"
						value={name}
						onChange={(e) => setName(e.target.value)}
						autoComplete="name"
						// biome-ignore lint/a11y/noAutofocus: the code step just unmounted — without this, focus falls to <body> and a screen reader loses the step change
						autoFocus
						className="mt-2 h-16 w-full rounded-2xl border-2 border-line bg-card px-4 text-xl outline-none focus:border-clay"
					/>

					<label className="mt-5 block font-medium" htmlFor="reg-firm">
						{copy.login.firmLabel}
						<span className="ml-2 text-sm font-normal text-ink-soft">
							{copy.login.optional}
						</span>
					</label>
					<input
						id="reg-firm"
						value={firmName}
						onChange={(e) => setFirmName(e.target.value)}
						autoComplete="organization"
						className="mt-2 h-16 w-full rounded-2xl border-2 border-line bg-card px-4 text-xl outline-none focus:border-clay"
					/>

					{/*
					 * Shown, not hidden behind a tick box: creating the account IS
					 * the consent act, and the version of these words travels with
					 * the request so we can say later what was on the screen.
					 */}
					<p className="mt-5 text-sm text-ink-soft">
						{copy.login.consent}{" "}
						<a
							href={PRIVACY_URL}
							target="_blank"
							rel="noreferrer"
							className="text-clay-dark underline"
						>
							{copy.login.consentLink}
						</a>
					</p>

					<ErrorLine message={error} />
					<PrimaryButton pending={verify.isPending}>
						{verify.isPending ? copy.login.verifying : copy.login.registerCta}
					</PrimaryButton>
				</form>
			)}
		</main>
	);
}

function PrimaryButton({
	pending,
	children,
}: {
	pending: boolean;
	children: React.ReactNode;
}) {
	return (
		<Button type="submit" full disabled={pending} className="mt-6">
			{children}
		</Button>
	);
}

function ErrorLine({ message }: { message: string | null }) {
	if (!message) return null;
	return (
		<p role="alert" className="mt-3 rounded-xl bg-bad-soft px-4 py-3 text-bad">
			{message}
		</p>
	);
}

function messageFor(error: unknown): string {
	if (error instanceof ApiError && error.status === 0)
		return copy.errors.network;
	return copy.errors.generic;
}
